import { forwardRef, useMemo, useState } from 'react';
import { Platform, StyleSheet, TextInput, View } from 'react-native'
import { ThemedFontSizes, ThemedValues, ThemedShared, ThemedFonts } from 'styles/themed';
import { colorList } from 'utils/colors';
import _ from 'lodash';

const Input = forwardRef( ({
    defaultValue,
    disabled,
    onChangeText,
    keyboardType,
    multiline = false,
    accessoryLeft,
    accessoryLeftWidth = 48,
    accessoryRight,
    accessoryRightWidth = 40,
    accessoryHeight = 48,
    error = false,
    style = {},
    containerStyle = {},
    onFocus,
    onBlur,
    ...textInputProps
}, ref ) => {

    const [ focused, setFocused ] = useState( false );
    const [ measuredRightWidth, setMeasuredRightWidth ] = useState( 0 );
    const autoRightWidth = accessoryRightWidth === 'auto';

    const multilineProps = useMemo(() => {
        if( !multiline ) {
            return {};
        }

        return {
            multiline: true,
            editable: true,
            numberOfLines: 8
        };
    }, [ multiline ]);

    const inputStyles = useMemo(() => {
        const result = [ styles.input ];

        if (multiline) {
            result.push(styles.inputMultiline);
        }

        if (disabled) {
            result.push(styles.inputDisabled);
        }

        return StyleSheet.flatten( result );
    }, [ multiline, disabled ]);

    const leftAccessoryContent = _.isFunction( accessoryLeft ) ? accessoryLeft() : accessoryLeft;
    const rightAccessoryContent = _.isFunction( accessoryRight ) ? accessoryRight() : accessoryRight;

    return (
        <View
            style={ [ styles.container, containerStyle ] }>

            <TextInput
                ref={ ref }
                style={[
                    inputStyles,
                    leftAccessoryContent ? { paddingLeft: accessoryLeftWidth } : null,
                    rightAccessoryContent ? { paddingRight: autoRightWidth ? measuredRightWidth : accessoryRightWidth } : null,
                    style,
                    focused ? styles.inputFocused : null,
                    error ? styles.inputError : null,
                ]}
                defaultValue={ _.toString( defaultValue ) } // Value is supposed to be a string! Otherwise won't render
                onChangeText={ onChangeText }
                keyboardType={ keyboardType || 'default' }
                editable={ !disabled }
                placeholderTextColor={ colorList.TEXT_HINT.primary }
                onFocus={ event => {
                    setFocused( true );
                    onFocus?.( event );
                }}
                onBlur={ event => {
                    setFocused( false );
                    onBlur?.( event );
                }}
                { ...textInputProps }
                { ...multilineProps }
            />

            { leftAccessoryContent && (
                <View style={[ styles.accessoryLeft, { width: accessoryLeftWidth, height: accessoryHeight } ]}>
                    { leftAccessoryContent }
                </View>
            )}

            { rightAccessoryContent && (
                <View
                    style={[
                        styles.accessoryRight,
                        autoRightWidth ? styles.accessoryRightAuto : { width: accessoryRightWidth },
                        { height: accessoryHeight },
                    ]}
                    onLayout={ autoRightWidth ? event => {
                        setMeasuredRightWidth( Math.ceil( event.nativeEvent.layout.width ) );
                    } : undefined }>
                    { rightAccessoryContent }
                </View>
            )}

            { disabled && (
                <View style={ styles.disabledOverlay } />
            )}
        </View>
    )
})

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'stretch',
    },
    accessoryRight: {
        position: 'absolute',
        right: 0,
        top: 0,
        height: 48,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
    },
    accessoryLeft: {
        position: 'absolute',
        left: 0,
        top: 0,
        height: 48,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
    },
    accessoryRightAuto: {
        // Fit the suffix while leaving room for typing on narrow screens.
        maxWidth: '60%',
        paddingHorizontal: 8,
    },
    input: {
        flex: 1,
        minHeight: 48,
        fontFamily: ThemedFonts.regular.fontFamily,
        fontSize: ThemedFontSizes.default.fontSize,
        // Let single-line iOS fields use their native font metrics for vertical centering.
        ...( Platform.OS === 'ios' ? {} : { lineHeight: ThemedFontSizes.default.fontSize * 1.5 } ),
        paddingVertical: 0,
        textAlignVertical: 'center',
        borderColor: colorList.GREY.dark,
        borderWidth: 1,
		paddingHorizontal: ThemedShared.paddingSmall.padding,
        borderRadius: ThemedValues.borderRadius,
        backgroundColor: colorList.WHITE.primary,
        color: colorList.TEXT.primary,
    },
    inputMultiline: {
        minHeight: 100,
        height: 'auto',
        lineHeight: ThemedFontSizes.default.fontSize * 1.5,
        textAlignVertical: 'top',
        paddingVertical: 10,
    },
    inputFocused: {
        borderColor: colorList.PRIMARY.dark,
        borderWidth: 2,
    },
    inputError: {
        borderColor: colorList.RED.primary,
    },
    inputDisabled: {
        backgroundColor: '#F2F2F2',
        borderColor: '#D1D1D6',
    },
    inputWithLabel: {
        paddingTop: 20, // Adjust based on label height
    },
    inputWithPlaceholder: {
        color: '#A1A1A6', // Placeholder color
    },

    disabledOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
    }
})

export default Input
